import { Suspense, useEffect, useState } from "react";
import Loading from "../../../common/components/ui/loading/Loading";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "../../../common/components/ui/table/TableFilters";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { formatDate } from "@/common/helpers/date.helpers";
import {
  IMaintenanceSchedule,
  MaintenanceScheduleStatus,
} from "../../../types/maintenanceSchedule.type";
import { ModalCreateMaintenanceSchedules } from "./ModalCreateMaintenanceSchedules";
import { ModalEditMaintenanceSchedules } from "./ModalEditMaintenanceSchedules";
import { ModalDeleteMaintenanceSchedules } from "./ModalDeleteMaintenanceSchedules";
import { getPagedMaintenanceSchedule } from "../../../common/services/maintenance-schedule.api";
import { useCompanyStore } from "@/common/states/store/Company";
import { getCompanyId } from "../../../common/services/company.api";
import { getCompanySigla } from "@/common/helpers/company.helpers";

function getMaintenanceTypeLabel(type?: string) {
  switch (type) {
    case "preventive":
      return "Revisão Preventiva";
    case "corrective":
      return "Revisão Corretiva";
    case "other":
      return "Outros";
    case "":
    case undefined:
    case null:
      return "-";
    default:
      return "-";
  }
}

const STATUS_LABELS = {
  [MaintenanceScheduleStatus.Scheduled]: "Agendado",
  [MaintenanceScheduleStatus.Completed]: "Concluído",
  [MaintenanceScheduleStatus.Canceled]: "Cancelado",
  [MaintenanceScheduleStatus.NoShow]: "Não Compareceu",
};

const STATUS_COLORS = {
  [MaintenanceScheduleStatus.Completed]: "font-semibold text-greenPrimary",
  [MaintenanceScheduleStatus.Scheduled]: "font-semibold text-gray",
  [MaintenanceScheduleStatus.Canceled]: "font-semibold text-redPrimary",
  [MaintenanceScheduleStatus.NoShow]: "font-semibold text-yellowPrimary",
};

const mapDataFormat = (data: IMaintenanceSchedule[]) => {
  return data.map((item) => ({
    companyId: getCompanySigla(item.companyId),
    typeReview: getMaintenanceTypeLabel(item.typeReview),
    licensePlate: item.licensePlate || "",
    renterName: item.renterName || "",
    renterPhone: item.renterPhone || "",
    status: getStatusLabels(item.status as MaintenanceScheduleStatus) || "",
    scheduledDate:
      formatDate(item.scheduledDate).concat(" ", item.scheduledTime) || "",
    _original: item,
  }));
};

const capitalizeName = (name: string): string => {
  return name
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const mapDataFormatFilter = (data: IMaintenanceSchedule[]) => {
  return data.map((item) => ({
    Cidade: getCompanySigla(item.companyId),
    Placa: item.licensePlate || "",
    "Nome do locatário": item.renterName ? capitalizeName(item.renterName) : "",
    Tipo: getMaintenanceTypeLabel(item.typeReview),
    "Data agendada":
      formatDate(item.scheduledDate).concat(" ", item.scheduledTime) || "",
    "Telefone do locatário": item.renterPhone || "",
    Status: STATUS_LABELS[item.status as MaintenanceScheduleStatus] || "",
    scheduledDate:
      formatDate(item.scheduledDate).concat(" ", item.scheduledTime) || "",
    Observações: item.notes || "",
  }));
};

function getStatusLabels(status: MaintenanceScheduleStatus) {
  return (
    <span
      className={`${STATUS_COLORS[status]}`}
      title={STATUS_LABELS[status] || status}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

const EMPTY_ACTION = { eventName: "", data: undefined };

function TableMaintenanceSchedules() {
  const company = useCompanyStore();
  const companyId = getCompanyId();
  const { notifySuccess, notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [rows, setRows] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [maintenanceSchedules, setMaintenanceSchedules] = useState<
    IMaintenanceSchedule[]
  >([]);
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-scheduledDate",
  });

  const headers = {
    companyId: "Cidade",
    licensePlate: "Placa",
    renterName: "Nome do locatário",
    scheduledDate: "Data agendada",
    typeReview: "Tipo de agendamento",
    status: "Status",
  };

  useEffect(() => {
    async function loadMaintenanceSchedule(filters, page) {
      try {
        const filtersCopy = { ...filters };
        if (filtersCopy.status.length === 0) {
          filtersCopy.status = [
            MaintenanceScheduleStatus.Scheduled
          ].join(",");
        }
        setIsLoading(true);
        setMaintenanceSchedules([]);

        const result = await getPagedMaintenanceSchedule({ ...filtersCopy, page });

        if (!result.ok) {
          return notifyError(
            result.message || "Erro ao carregar os agendamentos de manutenção"
          );
        }

        setRows({
          itemsPerPage: rows.itemsPerPage,
          totalItems: result.totalItems,
        });
        setMaintenanceSchedules(result.data as IMaintenanceSchedule[]);
      } catch (error) {
        notifyError("Erro ao carregar os agendamentos de manutenção");
      } finally {
        setIsLoading(false);
      }
    }

    loadMaintenanceSchedule(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, companyId, company]);

  const ACTION_MESSAGES = {
    edit: {
      [MaintenanceScheduleStatus.NoShow]:
        "Não é possível editar um agendamento com status 'Não Compareceu'.",
      [MaintenanceScheduleStatus.Completed]:
        "Não é possível editar um agendamento já concluído.",
      [MaintenanceScheduleStatus.Canceled]:
        "Não é possível editar um agendamento cancelado.",
      default: "Só é possível editar agendamentos com status 'Agendado'.",
    },
    delete: {
      [MaintenanceScheduleStatus.NoShow]:
        "Não é possível excluir um agendamento com status 'Não Compareceu'.",
      [MaintenanceScheduleStatus.Completed]:
        "Não é possível excluir um agendamento já concluído.",
      [MaintenanceScheduleStatus.Canceled]:
        "Não é possível excluir um agendamento cancelado.",
      default: "Só é possível excluir agendamentos com status 'Agendado'.",
    },
  };

  const actions = [
    {
      label: "Editar",
      action: (data) => {
        if (data._original.status !== MaintenanceScheduleStatus.Scheduled) {
          notifyError(
            ACTION_MESSAGES.edit[data._original.status] ||
            ACTION_MESSAGES.edit.default
          );
          return;
        }
        setAction({
          eventName: "editMaintenanceSchedule",
          data: data._original,
        });
      },
      permission: "edit:maintenance-schedule",
    },
    {
      label: "Excluir",
      action: (data) => {
        if (data._original.status !== MaintenanceScheduleStatus.Scheduled) {
          notifyError(
            ACTION_MESSAGES.delete[data._original.status] ||
            ACTION_MESSAGES.delete.default
          );
          return;
        }
        setAction({
          eventName: "deleteMaintenanceSchedule",
          data: data._original,
        });
      },
      permission: "edit:maintenance-schedule",
    },
  ];

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => setAction(EMPTY_ACTION);

  const addMaintenanceSchedule = (
    newSchedule: Omit<IMaintenanceSchedule, "_id">
  ) => {
    setIsLoading(true);
    try {
      const newId = `temp_${Date.now().toString()}`;

      const newItem: IMaintenanceSchedule = {
        _id: newId,
        ...newSchedule,
        createdAt: new Date(),
        updatedAt: new Date(),
        companyId: companyId || "0",
      };

      setMaintenanceSchedules((prev) => [newItem, ...prev]);

      notifySuccess("Agendamento criado com sucesso");
      return true;
    } catch (error) {
      console.error("Erro ao adicionar agendamento:", error);
      notifyError("Erro ao criar agendamento");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateMaintenanceSchedule = (
    updatedSchedule: IMaintenanceSchedule,
    scheduleId: string
  ) => {
    setIsLoading(true);
    try {
      const existingIndex = maintenanceSchedules.findIndex(
        (item) => item._id === scheduleId
      );
      if (existingIndex === -1) {
        notifyError("Agendamento não encontrado");
        return false;
      }

      const updatedItem = {
        ...updatedSchedule,
        updatedAt: new Date(),
        createdAt: maintenanceSchedules[existingIndex].createdAt,
      };

      const newArray = [...maintenanceSchedules];
      newArray[existingIndex] = updatedItem;
      setMaintenanceSchedules(newArray);

      notifySuccess("Agendamento atualizado com sucesso");
      return true;
    } catch (error) {
      console.error("Erro ao atualizar agendamento:", error);
      notifyError("Erro ao atualizar agendamento");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteMaintenanceSchedule = (scheduleId: string) => {
    setIsLoading(true);
    try {
      const filteredSchedules = maintenanceSchedules.filter(
        (item) => item._id !== scheduleId
      );

      if (filteredSchedules.length === maintenanceSchedules.length) {
        notifyError("Agendamento não encontrado");
        return false;
      }

      setMaintenanceSchedules(filteredSchedules);
      notifySuccess("Agendamento excluído com sucesso");
      return true;
    } catch (error) {
      console.error("Erro ao excluir agendamento:", error);
      notifyError("Erro ao excluir agendamento");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Agend. Manutenções"
          placeholder="Digite a placa ou o nome do locatário."
          statusOptions={STATUS_LABELS}
          newItemLabel="Novo Agendamento"
          onNewItemClick={() =>
            setAction({
              eventName: "createMaintenanceSchedule",
              data: undefined,
            })
          }
          onSearch={handleFilterChange}
          entity="maintenance-schedule"
          tableData={mapDataFormatFilter(maintenanceSchedules)}
        />

        <GenericTable
          headers={headers}
          data={mapDataFormat(maintenanceSchedules)}
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          entity="maintenance-schedule"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={rows.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />

        {isAction("createMaintenanceSchedule") && (
          <ModalCreateMaintenanceSchedules
            maintenanceSchedule={action.data as IMaintenanceSchedule}
            onClose={cleanActionData}
            onSave={(newSchedule) => {
              addMaintenanceSchedule(newSchedule);
              cleanActionData();
            }}
          />
        )}

        {isAction("editMaintenanceSchedule") && (
          <ModalEditMaintenanceSchedules
            maintenanceSchedule={action.data as IMaintenanceSchedule}
            onClose={cleanActionData}
            onSave={(updatedSchedule) => {
              const scheduleId = (action.data as IMaintenanceSchedule)._id;
              if (scheduleId) {
                updateMaintenanceSchedule(updatedSchedule, scheduleId);
              }
              cleanActionData();
            }}
          />
        )}

        {isAction("deleteMaintenanceSchedule") && (
          <ModalDeleteMaintenanceSchedules
            maintenanceSchedule={action.data as IMaintenanceSchedule}
            onRemove={() => {
              deleteMaintenanceSchedule(
                (action.data! as IMaintenanceSchedule)._id!
              );
              cleanActionData();
            }}
            onClose={cleanActionData}
          />
        )}
      </div>
    </Suspense>
  );
}

export default TableMaintenanceSchedules;
